/**
 * Financial-math helpers used by FX, inflation, and bond/interest tools.
 * Pure functions, no side effects, no Office.js — safe to unit-test.
 */

// ---------- LANDED COST ----------

export interface LandedCostInput {
  unitCount?: number;                   // Defaults to 1 (per-unit calc)
  exWorksPriceForeign: number;          // Supplier currency
  foreignFreight?: number;              // Foreign-currency freight
  insurance?: number;                   // Foreign-currency insurance
  fxRate: number;                       // AUD per 1 unit of foreign currency
  customsDutyPct?: number;              // 0.05 = 5%
  gstPct?: number;                      // 0.10 = 10% (default)
  brokerFee?: number;                   // AUD
  domesticFreight?: number;             // AUD
  warehousing?: number;                 // AUD
  otherCosts?: number;                  // AUD
}

export interface LandedCostBreakdown {
  cifForeign: number;
  cifAud: number;
  customsDuty: number;
  gst: number;
  totalAud: number;
  perUnitAud: number;
  marginAtSellPrice?: (sellPrice: number) => { gpDollar: number; gpPct: number };
  breakEvenFx?: (sellPrice: number) => number;  // FX rate at which margin = 0
}

export function calculateLandedCost(input: LandedCostInput): LandedCostBreakdown {
  const units = input.unitCount ?? 1;
  const exw = input.exWorksPriceForeign * units;
  const freight = (input.foreignFreight ?? 0) * units;
  const ins = (input.insurance ?? 0) * units;
  const cifForeign = exw + freight + ins;
  const cifAud = cifForeign * input.fxRate;

  const dutyPct = input.customsDutyPct ?? 0;
  const customsDuty = cifAud * dutyPct;

  const gstPct = input.gstPct ?? 0.10;
  const gst = (cifAud + customsDuty) * gstPct;

  const broker = input.brokerFee ?? 0;
  const domFreight = input.domesticFreight ?? 0;
  const warehousing = input.warehousing ?? 0;
  const other = input.otherCosts ?? 0;

  const totalAud = cifAud + customsDuty + gst + broker + domFreight + warehousing + other;
  const perUnitAud = totalAud / units;

  return {
    cifForeign,
    cifAud,
    customsDuty,
    gst,
    totalAud,
    perUnitAud,
    marginAtSellPrice: (sellPrice: number) => {
      const gpDollar = sellPrice - perUnitAud;
      const gpPct = sellPrice > 0 ? gpDollar / sellPrice : 0;
      return { gpDollar, gpPct };
    },
    breakEvenFx: (sellPrice: number) => {
      // Solve totalAud = sellPrice for FX rate (with all other inputs fixed)
      // Iterate — simple secant search
      let lo = 0.01, hi = 10;
      for (let i = 0; i < 60; i++) {
        const mid = (lo + hi) / 2;
        const test = calculateLandedCost({ ...input, fxRate: mid }).perUnitAud;
        if (test > sellPrice) hi = mid; else lo = mid;
        if (Math.abs(test - sellPrice) < 0.001) break;
      }
      return (lo + hi) / 2;
    },
  };
}

// ---------- INFLATION ----------

export function nominalToReal(values: number[], cpiIndex: number[], baseIndex: number = cpiIndex[0]): number[] {
  if (values.length !== cpiIndex.length) throw new Error('values and cpiIndex must be same length');
  return values.map((v, i) => v * (baseIndex / cpiIndex[i]));
}

export function applyCpiEscalation(baseValue: number, annualCpi: number[]): number[] {
  // baseValue is the "year 0" value; annualCpi[i] is the CPI rate for year i+1
  const out: number[] = [];
  let cur = baseValue;
  for (const rate of annualCpi) {
    cur = cur * (1 + rate);
    out.push(+cur.toFixed(2));
  }
  return out;
}

export function realHurdleRate(nominalRate: number, inflationRate: number): number {
  return (1 + nominalRate) / (1 + inflationRate) - 1;
}

// ---------- BOND VALUATION ----------

export interface BondInput {
  faceValue: number;
  couponRate: number;        // Annual decimal (e.g. 0.04)
  paymentsPerYear: number;   // 1 (annual), 2 (semi-annual), 4 (quarterly)
  yearsToMaturity: number;
  ytm: number;               // Annual decimal
}

export function bondPrice(b: BondInput): number {
  const periods = b.yearsToMaturity * b.paymentsPerYear;
  const periodicRate = b.ytm / b.paymentsPerYear;
  const periodicCoupon = b.faceValue * b.couponRate / b.paymentsPerYear;
  let pv = 0;
  for (let t = 1; t <= periods; t++) {
    pv += periodicCoupon / Math.pow(1 + periodicRate, t);
  }
  pv += b.faceValue / Math.pow(1 + periodicRate, periods);
  return pv;
}

export function macaulayDuration(b: BondInput): number {
  const periods = b.yearsToMaturity * b.paymentsPerYear;
  const periodicRate = b.ytm / b.paymentsPerYear;
  const periodicCoupon = b.faceValue * b.couponRate / b.paymentsPerYear;
  const price = bondPrice(b);
  let weightedTime = 0;
  for (let t = 1; t <= periods; t++) {
    const cf = periodicCoupon + (t === periods ? b.faceValue : 0);
    const pv = cf / Math.pow(1 + periodicRate, t);
    weightedTime += (t / b.paymentsPerYear) * pv;
  }
  return weightedTime / price;
}

export function modifiedDuration(b: BondInput): number {
  return macaulayDuration(b) / (1 + b.ytm / b.paymentsPerYear);
}

export function yieldToMaturity(price: number, faceValue: number, couponRate: number, paymentsPerYear: number, yearsToMaturity: number): number {
  // Bisection on YTM
  let lo = -0.10, hi = 1.00;
  for (let i = 0; i < 200; i++) {
    const mid = (lo + hi) / 2;
    const p = bondPrice({ faceValue, couponRate, paymentsPerYear, yearsToMaturity, ytm: mid });
    if (p > price) lo = mid; else hi = mid;
    if (Math.abs(p - price) < 0.0001) break;
  }
  return +(((lo + hi) / 2)).toFixed(6);
}

// ---------- DEBT / COVENANT ----------

export interface DebtFacility {
  facilityId: string;
  lender: string;
  balance: number;
  rate: number;            // Annual, decimal — for floating, this is current effective rate
  rateType: 'fixed' | 'floating';
  margin?: number;         // For floating: spread over reference (e.g. BBSW + 250bps → margin = 0.025)
  maturityYears: number;
}

export function totalInterestExpense(facilities: DebtFacility[], rateShock: number = 0): number {
  return facilities.reduce((sum, f) => {
    const effectiveRate = f.rateType === 'floating' ? f.rate + rateShock : f.rate;
    return sum + f.balance * effectiveRate;
  }, 0);
}

export function dscr(operatingCashflow: number, interest: number, principalRepayments: number): number {
  const debtService = interest + principalRepayments;
  return debtService > 0 ? operatingCashflow / debtService : Infinity;
}

export function interestCover(ebitda: number, interest: number): number {
  return interest > 0 ? ebitda / interest : Infinity;
}

export function netLeverage(netDebt: number, ebitda: number): number {
  return ebitda > 0 ? netDebt / ebitda : Infinity;
}

export interface CovenantTest {
  name: string;
  current: number;
  threshold: number;
  direction: 'min' | 'max';   // 'min' = must be >= threshold (e.g. DSCR ≥ 1.25)
  headroom: number;           // % away from breach
  breach: boolean;
}

export function testCovenants(input: {
  ebitda: number;
  operatingCashflow: number;
  interest: number;
  principalRepayments: number;
  netDebt: number;
  currentAssets: number;
  currentLiabilities: number;
  totalEquity: number;
}, thresholds: {
  dscrMin?: number;
  icrMin?: number;
  netLeverageMax?: number;
  currentRatioMin?: number;
  gearingMax?: number;
}): CovenantTest[] {
  const out: CovenantTest[] = [];
  if (thresholds.dscrMin != null) {
    const v = dscr(input.operatingCashflow, input.interest, input.principalRepayments);
    out.push({
      name: 'DSCR',
      current: +v.toFixed(2),
      threshold: thresholds.dscrMin,
      direction: 'min',
      headroom: ((v - thresholds.dscrMin) / thresholds.dscrMin) * 100,
      breach: v < thresholds.dscrMin,
    });
  }
  if (thresholds.icrMin != null) {
    const v = interestCover(input.ebitda, input.interest);
    out.push({
      name: 'Interest Cover',
      current: +v.toFixed(2),
      threshold: thresholds.icrMin,
      direction: 'min',
      headroom: ((v - thresholds.icrMin) / thresholds.icrMin) * 100,
      breach: v < thresholds.icrMin,
    });
  }
  if (thresholds.netLeverageMax != null) {
    const v = netLeverage(input.netDebt, input.ebitda);
    out.push({
      name: 'Net Leverage',
      current: +v.toFixed(2),
      threshold: thresholds.netLeverageMax,
      direction: 'max',
      headroom: ((thresholds.netLeverageMax - v) / thresholds.netLeverageMax) * 100,
      breach: v > thresholds.netLeverageMax,
    });
  }
  if (thresholds.currentRatioMin != null) {
    const v = input.currentLiabilities > 0 ? input.currentAssets / input.currentLiabilities : Infinity;
    out.push({
      name: 'Current Ratio',
      current: +v.toFixed(2),
      threshold: thresholds.currentRatioMin,
      direction: 'min',
      headroom: ((v - thresholds.currentRatioMin) / thresholds.currentRatioMin) * 100,
      breach: v < thresholds.currentRatioMin,
    });
  }
  if (thresholds.gearingMax != null) {
    const totalCap = input.netDebt + input.totalEquity;
    const v = totalCap > 0 ? input.netDebt / totalCap : 0;
    out.push({
      name: 'Gearing',
      current: +v.toFixed(3),
      threshold: thresholds.gearingMax,
      direction: 'max',
      headroom: ((thresholds.gearingMax - v) / thresholds.gearingMax) * 100,
      breach: v > thresholds.gearingMax,
    });
  }
  return out;
}

// ---------- NPV / IRR ----------

export function npv(rate: number, cashflows: number[]): number {
  return cashflows.reduce((acc, cf, t) => acc + cf / Math.pow(1 + rate, t), 0);
}

export function irr(cashflows: number[]): number | null {
  if (cashflows.length < 2) return null;
  let lo = -0.99, hi = 10;
  for (let i = 0; i < 200; i++) {
    const mid = (lo + hi) / 2;
    const val = npv(mid, cashflows);
    if (val > 0) lo = mid; else hi = mid;
    if (Math.abs(val) < 0.001) break;
  }
  const result = (lo + hi) / 2;
  return Number.isFinite(result) ? +result.toFixed(6) : null;
}

export function payback(cashflows: number[]): number | null {
  let cumulative = 0;
  for (let t = 0; t < cashflows.length; t++) {
    cumulative += cashflows[t];
    if (cumulative >= 0) {
      // Linear interpolation for fractional period
      const previousCumulative = cumulative - cashflows[t];
      if (cashflows[t] > 0) {
        return t - 1 + Math.abs(previousCumulative) / cashflows[t];
      }
      return t;
    }
  }
  return null;
}
